import { useState, useEffect } from "react";
import { useTheme } from "@material-ui/core";
import useTimer from "../../hooks/useTimer";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import styles from "./styles";

import MuteAudioButton from "../MuteAudioButton";
import MuteVideoButton from "../MuteVideoButton";
import RecordingButton from "../RecordingButton";
import LayoutButton from "../LayoutButton";
import ScreenSharingButton from "../ScreenSharingButton";
import EndCallButton from "../EndCallButton";
import MoreOptionsButton from "../MoreOptionsButton";
import PictureInPictureButton from "../PictureInPictureButton";

import PeopleIcon from "@material-ui/icons/People";
import { updateWaitingRoomStatus } from "../../api/updateWaitingRoomStatus";
import { retrieveToken } from "../../api/fetchCreds";

const isPipEnabled = process.env.REACT_APP_PIP_ENABLED === 'true';
const dataToLoad = retrieveToken();
const patientId = dataToLoad.patientId;


export default function ToolBar({
  room,
  connected,
  cameraPublishing,
  isScreenSharing,
  startScreenSharing,
  stopScreenSharing,
  participants,
  localParticipant,
  togglePIP,
  pipReady,
}) {
  const isChrome = navigator.userAgent.match(/chrome/i) !== null;
  const theme = useTheme();
  const { timer, handleStart } = useTimer(0);

  const formatTime = (timer) => {
    const getSeconds = `0${timer % 60}`.slice(-2);
    const minutes = `${Math.floor(timer / 60)}`;
    const getMinutes = `0${minutes % 60}`.slice(-2);
    return ` ${getMinutes}:${getSeconds}`;
  };

  const classes = styles();

  const [hasAudio, setHasAudio] = useState(true);
  const [hasVideo, setHasVideo] = useState(true);
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("xs"));

  const toggleVideo = () => {
    if (room && room.camera) {
      const { camera } = room;
      const isVideoEnabled = camera.isVideoEnabled();
      if (isVideoEnabled) {
        camera.disableVideo();
        setHasVideo(false);
        if (isChrome) navigator.mediaSession.setCameraActive(false);
      } else {
        camera.enableVideo();
        setHasVideo(true);
        if (isChrome) navigator.mediaSession.setCameraActive(true);
      }
    }
  };
  
  const toggleAudio = () => {
    if (room && room.camera) {
      const camera = room.camera;
      const isAudioEnabled = camera.isAudioEnabled();
      if (isAudioEnabled) {
        camera.disableAudio();
        setHasAudio(false);
        if (isChrome) navigator.mediaSession.setMicrophoneActive(false);
      } else {
        camera.enableAudio();
        setHasAudio(true);
        if (isChrome) navigator.mediaSession.setMicrophoneActive(true);
      }
    }
  };

  const getVideoSource = () => {
    if (room && room.camera) {
      return room.camera.getVideoDevice();
    }
  };

  const changeVideoSource = (videoId) => room.camera.setVideoDevice(videoId);

  const changeAudioSource = (audioId) => room.camera.setAudioDevice(audioId);

  const getAudioSource = async () => {
    if (room && room.camera) {
      const audioDevice = await room.camera.getAudioDevice();
      return audioDevice.deviceId;
    }
  };

  const endCall = () => {
    if (room) {
      const dataToLoad = retrieveToken();
      updateWaitingRoomStatus('momentarily-disconnected', dataToLoad.sessionId, dataToLoad.patientId);
      room.leave();
      window.top?.postMessage('ROFIM_VISIO_END_REQUESTED', '*');
      window.open("about:blank", "_self");
      window.close();
    }
  };

  useEffect(() => {
    if (connected) {
      const isAudioEnabled =
        room && room.camera && room.camera.isAudioEnabled() ? true : false;
      const isVideoEnabled =
        room && room.camera && room.camera.isVideoEnabled() ? true : false;
      setHasAudio(isAudioEnabled);
      setHasVideo(isVideoEnabled);
      if (isChrome) {
        navigator.mediaSession.setMicrophoneActive(isAudioEnabled);
        navigator.mediaSession.setCameraActive(isVideoEnabled);
      }
    }
  }, [connected, room]);

  useEffect(() => {
    handleStart();
    // Event close tab
    window.addEventListener('beforeunload', function (e) {
      const dataToLoad = retrieveToken();
      updateWaitingRoomStatus('momentarily-disconnected', dataToLoad.sessionId, dataToLoad.patientId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isChrome) {
    navigator.mediaSession.setActionHandler('togglecamera', () => {
      toggleVideo();
    });
    navigator.mediaSession.setActionHandler('togglemicrophone', () => {
      toggleAudio();
    });
    navigator.mediaSession.setActionHandler('hangup', () => {
      endCall();
    });
  }

  return isMobileWidth ? (
    <div className={classes.toolbarMobileContainer}>
      <MuteAudioButton
        toggleAudio={toggleAudio}
        hasAudio={hasAudio}
        classes={classes}
        changeAudioSource={changeAudioSource}
        localParticipant={localParticipant}
      />
      <EndCallButton classes={classes} handleEndCall={endCall} />
      <MuteVideoButton
        toggleVideo={toggleVideo}
        hasVideo={hasVideo}
        classes={classes}
        changeVideoSource={changeVideoSource}
      />
    </div>
  ) : (
    <div className={classes.toolbarContainer}>
      <div className={classes.leftSide}>
        <RecordingButton room={room} classes={classes} />
        <p className={classes.timerText}>{formatTime(timer)}</p>
        <span className={classes.timerText}>|</span>
        <div className={classes.centerWithSpacing}>
          <PeopleIcon />
          <p className={classes.centerWithSpacing}>{participants.length + 1}</p>
        </div>
      </div>
      <div className={classes.center}>
        <MuteAudioButton
          toggleAudio={toggleAudio}
          hasAudio={hasAudio}
          classes={classes}
          changeAudioSource={changeAudioSource}
          getAudioSource={getAudioSource}
          cameraPublishing={cameraPublishing}
          localParticipant={localParticipant}
        />
        <EndCallButton classes={classes} handleEndCall={endCall} />
        <MuteVideoButton
          toggleVideo={toggleVideo}
          hasVideo={hasVideo}
          classes={classes}
          getVideoSource={getVideoSource}
          cameraPublishing={cameraPublishing}
          changeVideoSource={changeVideoSource}
        />
      </div>
      <div className={classes.rightSide}>
        <ScreenSharingButton
          isScreenSharing={isScreenSharing}
          startScreenSharing={startScreenSharing}
          stopScreenSharing={stopScreenSharing}
          classes={classes}
        />
        {isPipEnabled && pipReady && !patientId && <PictureInPictureButton classes={classes} togglePIP={togglePIP} room={room} />}
        <LayoutButton classes={classes} room={room} />
        <MoreOptionsButton
          classes={classes}
          participants={participants}
          room={room}
          localParticipant={localParticipant}
        />
      </div>
    </div>
  );
}
